<template>
  <div class="h-full w-full flex flex-col">
    <div class="h-full container mx-auto">
      <div class="px-8 flex flex-col h-full w-full py-8">
        <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
          <div class="flex items-center text-sm font-light">
            <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
            <span class="mx-2">/</span>
            <div v-if="role != 'Clinical Placement Coordinator'">
              <router-link to="/training-module" class="opacity-50 hover:opacity-100">Internship</router-link>
              <span class="mx-2">/</span>
            </div>
            <span>Post Internship</span>
          </div>
          <div class="font-bold">Post Internship</div>
        </div>

        <section class="mt-8 h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
          <div class="flex justify-between flex-wrap">
            <h1 class="text-base md:text-lg font-semibold mb-4">
              Internship List
            </h1>
            <div class="flex justify-start items-center flex-wrap">
              <div v-if="role == 'admin' || role == 'superadmin'" class="flex justify-start items-center flex-wrap">
                <div class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                  <div class="text-sm mr-2">Entity:</div>
                  <select v-model="filterByEntity" class="outline-none text-sm flex-grow">
                    <option value="">All</option>
                    <option :value="item.entity" v-for="(item, index) in filteredEntities" :key="index">{{
                      getEntityName(item.entity) }}</option>
                  </select>
                </div>
                <div class="w-full sm:w-auto mb-4 border rounded-lg px-4 h-8 flex items-center">
                  <div class="text-sm mr-2">Employer:</div>
                  <select v-model="filterByPartner" class="outline-none text-sm w-full sm:w-auto">
                    <option value="">All Employers</option>
                    <option :value="item.id" v-for="(item, index) in partnersList" :key="index">{{ item.en_company_name
                      }}</option>
                  </select>
                </div>
                <div class="w-full sm:w-auto ml-0 sm:ml-8 mb-4">
                  <search @on-search="onSearch" class-list="px-4 h-8"></search>
                </div>
              </div>
              <div v-if="hasPermission('post-training-create')" @click.stop="createModal = true"
                class="ml-0 sm:ml-8 flex items-center bg-ternary hover:bg-white border-ternary border px-4 py-2 rounded-lg cursor-pointer text-white hover:text-ternary mb-4">
                <icon-plus class="w-4 h-4"></icon-plus>
                <span class="text-xs ml-2">Post an Internship</span>
              </div>
            </div>
          </div>

          <div class="flex flex-col" v-if="$apollo.queries.post_trainings.loading">
            <table-shimmer :titleHeaders="[
              '#',
              'Title',
              'Description',
              'Location',
              'Entity',
              'Start Date',
              'End Date',
              'No.of Applications',
              'View Applications',
              'Actions',
            ]" :rows="first"></table-shimmer>
          </div>
          <div v-else-if="!$apollo.queries.post_trainings.loading && !hasItems"
            class="h-full flex justify-center items-center">
            <h1 class="text-center text-xl font-medium">No internship found!</h1>
          </div>
          <div v-else class="flex flex-col">
            <div class="flex flex-col overflow-x-auto">
              <div class="inline-block min-w-full py-2">
                <div class="overflow-x-auto">
                  <table class="min-w-full text-left text-sm font-light">
                    <thead class="border-b font-medium dark:border-neutral-500">
                      <tr>
                        <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                          #
                        </th>
                        <th v-if="role == 'admin' || role == 'superadmin'" scope="col" class="px-6 py-2 text-gray-400">
                          Employer
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Title
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Description
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Location
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Entity
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Start Date
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          End Date
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          No.of Applications
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          View Applications
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in items" :key="index"
                        class="border-b border-dashed dark:border-neutral-500">
                        <td class="whitespace-nowrap px-6 py-2 font-medium">
                          {{ paginatorInfo.firstItem + index }}
                        </td>
                        <td v-if="role == 'admin' || role == 'superadmin'" class="whitespace-nowrap px-6 py-2">
                          <span class="font-medium">{{
                            item.partner ? item.partner.en_company_name : ""
                            }}</span>
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          <span class="font-medium">{{ item.title }}</span>
                        </td>
                        <td class="px-6 py-2 truncate-3">
                          {{ item.description }}
                        </td>
                        <td class="px-6 py-2">{{ item.location }}</td>
                        <td class="px-6 py-2">{{ item.entity }}</td>
                        <td class="px-6 py-2 text-center">
                          {{ getFormattedDate(item.start_date) }}
                        </td>
                        <td class="px-6 py-2 text-center">
                          {{ getFormattedDate(item.end_date) }}
                        </td>
                        <td class="px-6 py-2 text-center">
                          {{ item.application_count }}
                        </td>
                        <td class="px-6 py-2">
                          <div class="flex justify-center">
                            <div v-if="
                              hasPermission(
                                'post-training-view-applications'
                              ) && item.application_count
                            " @click.stop="
                              viewApplications(item.token, item.title)
                              "
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer">
                              <icon-document-list
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-document-list>
                            </div>
                          </div>
                        </td>
                        <td class="px-6 py-2">
                          <div class="flex">
                            <div v-if="hasPermission('post-training-show')" @click.stop="viewItem(item)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                              <icon-eye
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                            </div>
                            <div v-if="hasPermission('post-training-edit')" @click.stop="editItem(item)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                              <icon-pen
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-pen>
                            </div>
                            <div v-if="hasPermission('post-training-delete')" @click.stop="deleteItem(item.token)"
                              class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                              <icon-trash
                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-trash>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="mt-4">
              <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
            </div>
          </div>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="createModal" :title="`Create Internship`" @close="closeModal">
              <create class="text-left" :entities="filteredEntities" @created="onCreated" @close="closeModal"></create>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="editModal" :title="`Edit Internship - ${selectedItem.title}`" @close="closeModal">
              <edit class="text-left" @updated="onUpdated" @close="closeModal" :selected-item="selectedItem"
                :entities="filteredEntities"></edit>
            </base-full-modal>
          </transition>

          <transition name="fade" mode="out-in">
            <base-full-modal v-if="isViewItem" :title="`View Internship - ${selectedItem.title}`" height=""
              @close="closeModal">
              <view-post class="text-left" :item="selectedItem" @close="closeModal"></view-post>
            </base-full-modal>
          </transition>
        </section>
      </div>
    </div>
    <div class="h-full flex justify-center items-center" v-if="isLoading">
      <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>
  </div>
</template>

<script>
import IconEye from "../../components/icons/IconEye.vue";
import IconPen from "../../components/icons/IconPen.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
import IconDocumentList from "../../components/icons/IconDocumentList.vue";
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import TableShimmer from "../../components/shared/TableShimmer.vue";
import Create from "./create.vue";
import Edit from "./edit.vue";
import ViewPost from "./view.vue";
import { formattedDate } from "../../helpers/utils";
import Search from "../../components/shared/search.vue";
export default {
  name: "post-training-index",
  components: {
    IconEye,
    IconPen,
    IconTrash,
    IconPlus,
    IconDocumentList,
    Pagination,
    TableShimmer,
    Create,
    Edit,
    ViewPost,
    Search,
  },
  data() {
    return {
      post_trainings: [],
      first: 10,
      page: 1,
      createModal: false,
      editModal: false,
      isViewItem: false,
      selectedItem: null,
      filterByEntity: "",
      filterByPartner: "",
      partnersList: [],
      entities: [],
      isLoading: false,
      searchText: "",
    };
  },
  watch: {
    searchText() {
      this.page = 1;
    },
    post_trainings(newVal) {
      if (newVal.error) {
        if (newVal.error_code == 403) {
          this.$router.replace("/no-permission");
        } else if (newVal.error_code == 401) {
          this.$router.replace("/");
        } else {
          alert(newVal.message);
          this.$router.replace("/");
        }
      }
    },
    filterByEntity() {
      this.filterByPartner = "";
      this.page = 1;
      this.getPartnersList();
    },
    filterByPartner() {
      this.page = 1;
    },
  },
  computed: {
    items() {
      return this.post_trainings.data;
    },
    hasItems() {
      if (this.items && this.items.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    paginatorInfo() {
      return this.post_trainings.paginatorInfo;
    },
    role() {
      return this.$store.getters.role;
    },
    filteredEntities() {
      return this.entities.data;
    },
  },
  created() {
    if (this.role == "admin" || this.role == "superadmin") {
      this.getPartnersList();
    }
  },
  apollo: {
    post_trainings() {
      return {
        query: gql`
          query GetAllPostTrainings(
            $first: Int!
            $page: Int!
            $entity: String
            $filter_by_partner: Int
            $search: String
          ) {
            post_trainings(
              first: $first
              page: $page
              entity: $entity
              filter_by_partner: $filter_by_partner
              search: $search
            ) {
              error
              error_code
              message
              data {
                id
                token
                title
                description
                trainer_information
                start_date
                end_date
                location
                duration
                entity
                prerequisites
                additional_information
                application_count
                partner {
                  en_company_name
                }
              }
              paginatorInfo {
                count
                currentPage
                firstItem
                hasMorePages
                lastItem
                lastPage
                perPage
                total
              }
            }
          }
        `,
        variables() {
          return {
            first: this.first,
            page: this.page,
            entity: this.filterByEntity,
            filter_by_partner: parseInt(this.filterByPartner),
            search: this.searchText,
          };
        },
        fetchPolicy: "cache-and-network",
      };
    },
    entities() {
      return {
        query: gql`
          query GetEntityList {
            entities {
              data {
                id
                entity
              }
            }
          }
        `,
        fetchPolicy: "cache-and-network",
      };
    },
  },
  methods: {
    onSearch(searchText) {
      this.searchText = searchText;
    },
    getEntityName(entity) {
      return entity.replace(/ATHS/g, "ATS");
    },
    setPage(page) {
      this.page = page;
    },
    closeModal() {
      this.createModal = false;
      this.editModal = false;
      this.isViewItem = false;
      this.selectedItem = null;
    },
    showToast(message = "") {
      this.$apollo.queries.post_trainings.refetch();
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", this.$swal.stopTimer);
          toast.addEventListener("mouseleave", this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: "success",
        title: message,
      });
      this.closeModal();
    },
    onCreated(msg) {
      this.showToast(msg);
    },
    editItem(item) {
      this.selectedItem = item;
      this.editModal = true;
    },
    async deleteItem(token) {
      let error_msg = null;
      let success_msg = null;
      this.$swal
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            const DELETE_MUTATION = gql`
              mutation DeletePostTraining($token: String!) {
                deletePostTraining(token: $token) {
                  error
                  message
                }
              }
            `;

            try {
              await this.$apollo
                .mutate({
                  mutation: DELETE_MUTATION,
                  variables: {
                    token: token,
                  },
                })
                .then((data) => {
                  if (data.data.deletePostTraining.error) {
                    error_msg = data.data.deletePostTraining.message;
                  } else {
                    success_msg = data.data.deletePostTraining.message;
                  }
                })
                .catch((error) => {
                  error_msg = error.message;
                });
            } catch (error) {
              error_msg = error.message || "Please try again.";
            }

            if (error_msg === null) {
              this.showToast(success_msg);
            } else {
              this.$swal
                .fire({
                  icon: "error",
                  title: "Oops...",
                  text: error_msg,
                })
                .then((result) => {
                  if (result.value || result.dismiss) {
                    error_msg = null;
                  }
                });
            }
          }
        });
    },
    onUpdated(msg) {
      this.showToast(msg);
    },
    hasPermission(crudName) {
      if (this.role == "superadmin") {
        return true;
      } else {
        const permissions = this.$store.getters.permissions;
        const permissionsArray = permissions.split(",");
        if (permissionsArray.includes(crudName)) {
          return true;
        } else {
          return false;
        }
      }
    },
    viewItem(item) {
      this.selectedItem = item;
      this.isViewItem = true;
    },
    viewApplications(token) {
      this.$router.replace(`/post-training/applications/${token}`);
    },
    async getPartnersList() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetActivePartnersList($entity: String) {
              activePartners(entity: $entity) {
                data {
                  id
                  en_company_name
                }
              }
            }
          `,
          variables: {
            entity: this.filterByEntity,
          },
          fetchPolicy: "network-only",
        });
        this.partnersList = data.activePartners.data;
      } catch (error) {
        console.log(error);
      }
    },
    getFormattedDate(date) {
      return formattedDate(date);
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.truncate-3 {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}
</style>
